<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">Proprietário Frota (TCE)</h1>
        <p class="text-sm text-slate-500">Consulta e geração de arquivos para o Tribunal de Contas</p>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2 uppercase tracking-widest text-[11px]">
                  <v-icon color="orange" small class="mr-2">mdi-magnify</v-icon>
                  FILTRAR CONSULTA
              </h2>
          </div>
          <div class="p-8">
              <div class="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                  <!-- Mês e Ano -->
                  <div class="md:col-span-3">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Mês e Ano</label>
                      <input 
                        v-model="filter.date"
                        type="month"
                        class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors h-[40px]"
                      >
                  </div>

                  <!-- Prefeitura -->
                  <div class="md:col-span-6">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                      <v-autocomplete
                        v-model="filter.prefecture_id"
                        :items="prefectures"
                        item-value="prefecture_id"
                        item-text="prefecture_social_name"
                        placeholder="Selecione a prefeitura..."
                        outlined
                        dense
                        hide-details
                        @change="getCode(filter.prefecture_id)"
                        class="custom-select"
                      />
                  </div>

                  <!-- Unidade Gestora -->
                  <div class="md:col-span-3">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Unidade Gestora</label>
                      <v-autocomplete
                        v-model="filter.cod"
                        :items="codigos"
                        item-value="cod"
                        item-text="cod"
                        placeholder="Código..."
                        outlined
                        dense
                        hide-details
                        class="custom-select"
                      />
                  </div>

                  <!-- Ações de Filtro -->
                  <div class="md:col-span-12 flex justify-end gap-3 mt-4 pt-6 border-t border-slate-100">
                      <v-btn
                        :to="`/report/tce/proprietario-frota/${filter.date?.substring(5)}/${filter.date?.substring(0, 4)}/${filter.prefecture_id}/${filter.cod}`"
                        target="_blank"
                        depressed
                        :disabled="!filter.prefecture_id || !filter.cod || !filter.date"
                        class="bg-slate-100 text-slate-600 font-bold text-[11px] px-6 h-10 rounded-lg uppercase tracking-widest hover:bg-slate-200 transition-all shadow-none"
                      >
                        Gerar Relatório
                      </v-btn>
                      <button 
                        @click="search"
                        :disabled="isButtonDisabled || loader"
                        class="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold text-[11px] px-10 py-2 rounded-lg shadow-md shadow-orange-500/20 hover:shadow-lg transform hover:-translate-y-0.5 transition-all uppercase tracking-widest flex items-center justify-center gap-2 h-10"
                      >
                        <v-icon small color="white" style="font-size: 14px;">{{ loader ? 'mdi-loading' : 'mdi-filter-variant' }}</v-icon>
                        {{ loader ? 'FILTRANDO...' : 'FILTRAR' }}
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- RESULTADO -->
      <section v-if="data.length > 0 || loader" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2 uppercase tracking-widest text-[11px]">
                  <v-icon color="orange" small class="mr-2">mdi-table-large</v-icon>
                  RESULTADO DA CONSULTA
              </h2>
              <button 
                @click="exportFile"
                class="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-6 py-2 rounded-lg border border-white/20 transition-all uppercase tracking-widest flex items-center gap-2"
              >
                <v-icon small color="white">mdi-download</v-icon>
                Exportar
              </button>
          </div>
          <div class="p-0">
              <v-data-table 
                :loading="loader" 
                :items="data" 
                :headers="headers"
                class="custom-table"
                hide-default-footer
                :items-per-page="-1"
              />
          </div>
      </section>
    </div>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  mixins: [Helpers],
  data() {
    return {
      loader: false,
      data: [],
      prefectures: [],
      codigos: [],
      filter: {
        prefecture_id: null,
        date: null,
        cod: null,
      },
      headers: [
        { text: "Código da Unidade Gestora", value: "codUnidadeGestora" },
        { text: "Cpf/Cnpj do Proprietário", value: "cpfcnpj" },
        { text: "Nome do Proprietário", value: "nome" },
      ],
    };
  },
  created() {
    this.Helpers;
    this.initialize();
  },
  computed: {
    isButtonDisabled() {
      return !(
        this.filter.prefecture_id &&
        this.filter.date &&
        this.filter.cod
      );
    },
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
      this.loader = false;
      this.$store.dispatch("setLoader", this.loader);
    },
    getCode(prefecture_id) {
      this.$http(`/tce/codigo/${prefecture_id}`).then(
        (r) => (this.codigos = r.data)
      );
    },
    search() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(
          `/tce/proprietario-frota/${this.filter.prefecture_id}/${this.filter.cod}`
        )
        .then((res) => {
          this.data = res.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          alert(e.message);
        });
    },
    exportFile() {
      const element = document.createElement("a");
      const content = this.data.map((value, index) => {
        if (index > 0) {
          return `\n${value.export}`;
        }
        return value.export;
      });

      const file = new Blob(content, { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${this.filter.cod}${this.filter.date.substring(
        5
      )}${this.filter.date.substring(0, 4)}ProprietarioFrota.txt`;
      document.body.appendChild(element);
      element.click();
    },
  },
};
</script>

<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.w-full { width: 100%; }
.p-4 { padding: 1rem; }

/* Custom Table Override */
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep thead { background-color: #f8fafc !important; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
}

/* Custom Select Override for Vuetify Outlined */
.custom-select ::v-deep .v-input__control > .v-input__slot {
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
}
.custom-select ::v-deep .v-text-field__details {
  display: none !important;
}
</style>
